import {
  LinkAuthenticationElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { StripeError } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import {
  Suspense,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type GestureResponderEvent, View } from 'react-native';

import { Button, Heading, Spinner, Text, VStack } from '@squire/ui';

import { getPaymentIntentQuery } from '@/api/booking/getPaymentIntentQuery';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { StripeProvider } from '@/providers/StripeProvider';
import { Logger } from '@/utils/logging';
import { PaymentMethodType } from '@/utils/paymentMethods';
import { isUnmountedElementError } from '@/utils/stripeErrors';

import { styles } from './styles';
import type { CheckoutFormBottomSheetProps } from './types';

// Exported so callers that mount their own StripeProvider (e.g. the standby
// inline card step) reuse the exact same exclusion list instead of duplicating it.
export const EXCLUDED_PAYMENT_METHODS = [
  'klarna',
  'cashapp',
  'afterpay_clearpay',
  'affirm',
];

/**
 * Logs only the error's type and codes: a Stripe error carries the payment
 * method (card brand, last4, expiry, billing email). Card declines and
 * incomplete fields are the customer's input, shown inline by the
 * PaymentElement, so they are warnings.
 */
const logStripeError = (message: string, error: StripeError) => {
  const context = {
    type: error.type,
    code: error.code,
    declineCode: error.decline_code,
  };
  if (error.type === 'card_error' || error.type === 'validation_error') {
    Logger.warn(message, context);
  } else {
    Logger.error(message, context);
  }
};

const CheckoutFormBottomSheetContent = ({
  customer,
  onSubmit,
  isWaitlistMode = false,
  isGiftCardFlow = false,
  isSubmitting = false,
  isPayInPerson = false,
  isBooking = false,
  submitLabel,
}: CheckoutFormBottomSheetProps) => {
  const { t } = useScreenTranslations('CheckoutFormBottomSheet');
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const formRef = useRef<HTMLFormElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  // Stripe rejects submit/confirm on a PaymentElement that has not loaded.
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const hasConsumedIntentRef = useRef(false);

  // SQR-15545: a confirmed SetupIntent can't be reused — Stripe rejects a retry
  // against it — so drop it once consumed. Evicting while mounted remounts the
  // form (StripeProvider suspends), so the booking flow defers this to release
  // or unmount; callers that show nothing in flight evict right after submit.
  const evictConsumedIntent = useCallback(() => {
    if (!hasConsumedIntentRef.current) return;
    hasConsumedIntentRef.current = false;
    queryClient.removeQueries({
      queryKey: getPaymentIntentQuery(EXCLUDED_PAYMENT_METHODS).queryKey,
      exact: true,
    });
  }, [queryClient]);

  useEffect(() => () => evictConsumedIntent(), [evictConsumedIntent]);

  // After a successful submit the button keeps loading until the sheet closes;
  // release it once the parent is neither booking nor submitting (a failed
  // attach, or a failed gift-card purchase) and evict for the retry.
  useEffect(() => {
    if (isBooking || isSubmitting) return;
    setIsLoading(false);
    evictConsumedIntent();
  }, [isBooking, isSubmitting, evictConsumedIntent]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe) {
      Logger.error("Stripe.js hasn't yet loaded.");
      return;
    }
    if (!elements) {
      Logger.error(
        'CheckoutFormBottomSheet is not mounted inside a StripeProvider.',
      );
      return;
    }
    if (!isPaymentElementReady) return;

    setIsLoading(true);
    // Keep our spinner past onSubmit only if the parent already shows the flow
    // in flight; otherwise nothing would ever release it.
    const parentInFlight = isBooking || isSubmitting;

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setIsLoading(false);
        return logStripeError('Stripe form was not submitted', submitError);
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (confirmError) {
        setIsLoading(false);
        return logStripeError('Stripe setup was not confirmed', confirmError);
      }

      hasConsumedIntentRef.current = true;

      if (setupIntent?.payment_method) {
        const paymentMethodId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent.payment_method.id;

        let paymentMethodDetails =
          typeof setupIntent.payment_method === 'object'
            ? setupIntent.payment_method
            : null;

        // When Stripe returns the PM as a string id, retrieve the full
        // setup intent to get expanded card details (brand, last4).
        if (!paymentMethodDetails && setupIntent.client_secret) {
          try {
            const { setupIntent: expanded } = await stripe.retrieveSetupIntent(
              setupIntent.client_secret,
            );
            if (expanded && typeof expanded.payment_method === 'object') {
              paymentMethodDetails = expanded.payment_method;
            }
          } catch {
            // Non-critical — proceed without card details
          }
        }

        if (paymentMethodDetails?.card) {
          if (!parentInFlight) {
            setIsLoading(false);
            evictConsumedIntent();
          }
          return onSubmit?.({
            type: PaymentMethodType.card,
            id: `${paymentMethodDetails.card.brand}-${paymentMethodDetails.card.last4}`,
            token: paymentMethodId,
            cardDetails: {
              brand: paymentMethodDetails.card.brand,
              last4: paymentMethodDetails.card.last4,
            },
          });
        }

        if (!parentInFlight) {
          setIsLoading(false);
          evictConsumedIntent();
        }
        return onSubmit?.({
          type: PaymentMethodType.card,
          id: 'card',
          token: paymentMethodId,
        });
      }

      setIsLoading(false);
      Logger.error('Setup intent confirmed but no payment method found');
    } catch (error) {
      setIsLoading(false);
      if (isUnmountedElementError(error)) {
        Logger.warn('Stripe card field was not available', {
          message: (error as Error).message,
        });
        return;
      }
      Logger.error('An error occurred while confirming the setup intent', {
        error,
      });
    }
  };

  const canSubmit =
    !!stripe && !!elements && isPaymentElementReady && !isLoading;

  const handlePressSubmit = (e: GestureResponderEvent) => {
    e.preventDefault();
    submitButtonRef.current?.click();
  };

  const loadingLabel = isGiftCardFlow ? t('buyingGiftCard') : t('booking');

  if (isSubmitting) {
    return (
      <VStack flex style={[styles.content, styles.submittingContent]}>
        <View style={styles.loadingContent}>
          <Text color="textPrimary" variant="lStrong">
            {loadingLabel}
          </Text>
          <Spinner color="textPrimary" size="small" />
        </View>
      </VStack>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <VStack flex style={styles.content}>
        <Heading variant="m">
          {isPayInPerson ? t('cardOnFileRequired') : t('cardDetails')}
        </Heading>

        <LinkAuthenticationElement
          options={{
            defaultValues: {
              email: customer?.email || '',
            },
          }}
        />

        <PaymentElement
          id="payment-element"
          onReady={() => setIsPaymentElementReady(true)}
          options={{
            defaultValues: {
              billingDetails: {
                email: customer?.email || '',
                phone: customer?.phone || '',
                name: `${customer?.firstName || ''} ${customer?.lastName || ''}`,
              },
            },
            wallets: {
              applePay: 'never',
              googlePay: 'never',
              link: 'auto',
            },
            layout: 'tabs',
            paymentMethodOrder: ['card'],
          }}
        />

        <button
          hidden
          type="submit"
          ref={submitButtonRef}
          disabled={!canSubmit}
        />

        <Button
          disabled={!canSubmit}
          onPress={handlePressSubmit}
          variant="primary"
          size="medium"
          accessibilityRole="button"
          aria-disabled={!canSubmit || undefined}
        >
          {isLoading ? (
            <View style={styles.loadingContent}>
              <Text color="textPrimaryInverse" variant="lStrong">
                {loadingLabel}
              </Text>
              <Spinner color="textPrimaryInverse" size="small" />
            </View>
          ) : (
            <Text color="textPrimaryInverse" variant="lStrong">
              {submitLabel ??
                (isWaitlistMode
                  ? t('joinWaitlist')
                  : isGiftCardFlow
                    ? t('buyGiftCard')
                    : t('book'))}
            </Text>
          )}
        </Button>
      </VStack>
    </form>
  );
};

const CheckoutFormBottomSheet = forwardRef<
  BottomSheetRef,
  CheckoutFormBottomSheetProps
>(({ onDismiss, ...props }, ref) => {
  const { t } = useScreenTranslations('CheckoutFormBottomSheet');
  return (
    // Wire onDismiss to the sheet: on web the form is wrapped in the shared
    // BottomSheet, whose swipe-down / backdrop dismissal does not go through
    // onCancel — without this, that path leaves the caller's in-flight state stuck.
    <BottomSheet
      ref={ref}
      onDismiss={onDismiss}
      accessibilityLabel={
        props.isPayInPerson ? t('cardOnFileRequired') : t('cardDetails')
      }
    >
      <Suspense fallback={<Spinner />}>
        <StripeProvider excludePaymentMethods={EXCLUDED_PAYMENT_METHODS}>
          <CheckoutFormBottomSheetContent {...props} />
        </StripeProvider>
      </Suspense>
    </BottomSheet>
  );
});

CheckoutFormBottomSheet.displayName = 'CheckoutFormBottomSheet';
// Exported additively so a desktop-only caller can render the Stripe form
// inline (its own chrome, e.g. a drawer step) without the shared BottomSheet.
export { CheckoutFormBottomSheet, CheckoutFormBottomSheetContent };
